// OnboardingPage.js
// Collects additional user information after signup
// Shows only for users who signed up but haven't completed onboarding

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

function OnboardingPage({ user, onOnboardingComplete }) {
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [tasksToOffload, setTasksToOffload] = useState('');
  const [howHeard, setHowHeard] = useState('');

  // Error and loading states
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // How did you hear about us options
  const hearAboutOptions = [
    { value: '', label: 'Select an option' },
    { value: 'friend', label: 'Friend or family' },
    { value: 'social', label: 'Social media' },
    { value: 'search', label: 'Search engine' },
    { value: 'ad', label: 'Advertisement' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (firstName.trim().length < 1) {
      setError('Please enter your first name.');
      setLoading(false);
      return;
    }

    if (lastName.trim().length < 1) {
      setError('Please enter your last name.');
      setLoading(false);
      return;
    }

    if (zipcode.trim().length < 5) {
      setError('Please enter a valid zipcode.');
      setLoading(false);
      return;
    }

    if (!howHeard) {
      setError('Please tell us how you heard about us.');
      setLoading(false);
      return;
    }

    try {
      // Update user profile in Firestore with onboarding data
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        zipcode: zipcode.trim(),
        tasksToOffload: tasksToOffload.trim(),
        howHeard: howHeard,
        onboardingComplete: true
      });

      // Refresh the user profile in App.js
      if (onOnboardingComplete) {
        await onOnboardingComplete();
      }

      // Navigate to family setup
      navigate('/family-setup');
    } catch (err) {
      console.error('Onboarding error:', err);
      setError('Failed to save your information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-header">
        <h1>Tell us about yourself</h1>
        <p>Help us personalize your Kana experience</p>
      </div>

      <form onSubmit={handleSubmit} className="onboarding-form">
        {/* First name */}
        <div className="form-group">
          <label htmlFor="firstName">First name</label>
          <input
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Enter your first name"
            required
          />
        </div>

        {/* Last name */}
        <div className="form-group">
          <label htmlFor="lastName">Last name</label>
          <input
            type="text"
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Enter your last name"
            required
          />
        </div>

        {/* Zipcode */}
        <div className="form-group">
          <label htmlFor="zipcode">Zipcode</label>
          <input
            type="text"
            id="zipcode"
            value={zipcode}
            onChange={(e) => setZipcode(e.target.value)}
            placeholder="Enter your zipcode"
            maxLength={10}
            required
          />
        </div>

        {/* Tasks to offload */}
        <div className="form-group">
          <label htmlFor="tasksToOffload">What tasks would you like to offload?</label>
          <textarea
            id="tasksToOffload"
            value={tasksToOffload}
            onChange={(e) => setTasksToOffload(e.target.value)}
            placeholder="Tell us about the tasks you'd like help with (e.g., planning dates, scheduling appointments, researching travel itineraries...)"
            rows={4}
          />
        </div>

        {/* How did you hear about us */}
        <div className="form-group">
          <label htmlFor="howHeard">How did you hear about us?</label>
          <select
            id="howHeard"
            value={howHeard}
            onChange={(e) => setHowHeard(e.target.value)}
            required
          >
            {hearAboutOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Show error if there is one */}
        {error && <p className="form-error">{error}</p>}

        {/* Submit button */}
        <button
          type="submit"
          className="button button-primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Continue'}
        </button>
      </form>
    </div>
  );
}

export default OnboardingPage;
